import { CtResponse } from "../Fetch";
import { xget, xhttp } from "../Fetchv2";

export interface KPIData {
	id?: string;
	value: string | number;
	change?: string | number;
	changeType?: "positive" | "negative" | "neutral";
}

export interface AlertData {
	id: string;
	time: string | number;
	total: number;
	priority: "high" | "medium" | "low";
}

export interface ChartsData {
	funnelData: {
		series: { data: number[] }[];
	};
	approvalData: {
		series: { data: number[] }[];
	};
	rejectionData: {
		series: { data: number[] }[];
		labels?: string[];
	};
	correlationData: {
		series: { data: number[] }[];
	};
	riskData: {
		series: number[];
	};
	trendData: {
		series: number[];
		labels: string[];
	};
	// Extended fields returned by backend (not all are consumed yet)
	jobsByModality?: {
		labels: string[];
		series: number[];
	};
	candidatesByGender?: {
		labels: string[];
		series: number[];
	};
	candidatesByStudies?: {
		labels: string[];
		series: number[];
	};
	topSkills?: {
		labels: string[];
		series: number[];
	};
	utmSources?: {
		labels: string[];
		series: number[];
	};
	phaseCounts?: { phase: number; count: number }[];
}

export interface TablesData {
	jobsLeaderboard: {
		job_id: number;
		title: string;
		views: number;
		vacancies: number;
		inscribed: number;
		hires: number;
		conversion: number;
	}[];
	jobsAtRisk: {
		job_id: number;
		title: string;
		inscribed: number;
		vacancies: number | null;
		close: string | null;
		days_left: number;
	}[];
	utmCampaigns: {
		campaign: string;
		applications: number;
	}[];
}

export class Dashboard {
	// Obtener todos los datos
	async getDashboards(): Promise<CtResponse[]> {
		let response = await xget("/dashboards");
		return response;
	}

	// Obtener los datos por PGID
	async getDashboardsById(id: string): Promise<CtResponse[]> {
		let response = await xget(`/dashboard/${id}`);
		return response;
	}

	// Actualizar parametros de la pagina
	async updateDocument(id: string, data: any): Promise<CtResponse<{ ok: number | undefined }>> {
		let response = await xhttp(`/dashboard/${id}`, "PUT", { data });
		return response;
	}

	// Obtener analiticas
	async getAnalytics(arrPagesID: string[]): Promise<CtResponse[]> {
		let res = await xhttp(`/dashboard`, "SEARCH", { pages: arrPagesID });
		return res;
	}

	// Obtener resultados busqueda
	async searchByName(name: string): Promise<CtResponse[]> {
		let res = await xhttp("/dashboard/search/name", "SEARCH", { name });
		return res;
	}

	// Obtener filtro por costo
	async searchByCost(min: number, max: number): Promise<CtResponse[]> {
		let res = await xhttp("/dashboard/search/cost", "SEARCH", { min, max });
		return res;
	}

	// Obtener PrePage
	async getPrePages(pgid: string): Promise<CtResponse[]> {
		let res = await xget(`/dashboard/${pgid}/prepage`);
		return res;
	}

	// Create a new PrePage
	// NOTE: DashboardPrePage type is not available in current schemas, so we keep this loosely typed.
	async createPrePage(newPrePage: any): Promise<CtResponse<{ ok: number | undefined; prePage: any }>> {
		let res = await xhttp("/dashboard/create", "POST", { newPrePage });
		return res;
	}

	// Actualizar parametros de la prepagina
	async updateDocumentPrepage(id: string, data: any): Promise<CtResponse<{ ok: number | undefined }>> {
		let response = await xhttp(`/dashboard/${id}/prepage`, "PUT", { data });
		return response;
	}

	// Optional filter params for dashboard data
	getDashboardQueryParams(
		period?: string,
		filters?: { dateFrom?: string; dateTo?: string; jobId?: string | number; entityId?: string; branchId?: string; status?: string }
	) {
		const params: Record<string, string | number | undefined> = { period };
		if (filters?.dateFrom) params.dateFrom = filters.dateFrom;
		if (filters?.dateTo) params.dateTo = filters.dateTo;
		if (filters?.jobId != null && String(filters.jobId).trim() !== "") params.jobId = filters.jobId;
		if (filters?.entityId?.trim()) params.entityId = filters.entityId;
		if (filters?.branchId?.trim()) params.branchId = filters.branchId;
		if (filters?.status?.trim()) params.status = filters.status;
		return params;
	}

	// Obtener KPIs
	async getKPIs(
		pgid: string,
		period?: string,
		filters?: { dateFrom?: string; dateTo?: string; jobId?: string | number; entityId?: string; branchId?: string; status?: string }
	): Promise<CtResponse<KPIData[]>> {
		let response = await xget<KPIData[]>(`/pages/${pgid}/dashboard/kpis`, this.getDashboardQueryParams(period, filters));
		return response;
	}

	// Obtener Alertas
	async getAlerts(
		pgid: string,
		period?: string,
		filters?: { dateFrom?: string; dateTo?: string; jobId?: string | number; entityId?: string; branchId?: string; status?: string }
	): Promise<CtResponse<AlertData[]>> {
		let response = await xget<AlertData[]>(`/pages/${pgid}/dashboard/alerts`, this.getDashboardQueryParams(period, filters));
		return response;
	}

	// Obtener Gráficas
	async getCharts(
		pgid: string,
		period?: string,
		filters?: { dateFrom?: string; dateTo?: string; jobId?: string | number; entityId?: string; branchId?: string; status?: string }
	): Promise<CtResponse<ChartsData>> {
		let response = await xget<ChartsData>(`/pages/${pgid}/dashboard/charts`, this.getDashboardQueryParams(period, filters));
		return response;
	}

	// Obtener Tablas
	async getTables(
		pgid: string,
		period?: string,
		filters?: { dateFrom?: string; dateTo?: string; jobId?: string | number; entityId?: string; branchId?: string; status?: string }
	): Promise<CtResponse<TablesData>> {
		let response = await xget<TablesData>(`/pages/${pgid}/dashboard/tables`, this.getDashboardQueryParams(period, filters));
		return response;
	}
}
